import React, { useEffect } from "react";
import { useTable, usePagination } from "react-table";
import createPagination from "../Hooks/usePagination";
import { MdCompareArrows } from "react-icons/md";

const CustomTable = ({
    columns,
    data,
    updatePagination,
    paginationState,
    pageCount: controlledPageCount,
    sortable,
    hidePagination,
}) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        gotoPage,
        previousPage,
        nextPage,
        pageCount,
        canNextPage,
        canPreviousPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            manualPagination: true,
            pageCount: controlledPageCount,
            initialState: {
                pageIndex: 0,
            },
        },
        usePagination
    );

    useEffect(() => {
        if (updatePagination) {
            updatePagination((prev) => ({
                ...prev,
                page: pageIndex + 1,
            }));
        }
    }, [updatePagination, pageIndex, pageSize]);

    const { pagination } = createPagination({
        numberOfButtons: 2,
        currentPage: paginationState ? paginationState.page : pageIndex + 1,
        totalPages: paginationState
            ? paginationState.totalPages
                ? paginationState.totalPages
                : controlledPageCount
            : controlledPageCount,
    });

    return (
        <div className="table-wrapper">
            <table {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()}>
                                    <div className="flex items-center">
                                        {column.render("Header")}
                                        {sortable && sortable.includes(column.id) && (
                                            <div className="cursor-pointer ml-2">
                                                <MdCompareArrows
                                                    onClick={() => {
                                                        updatePagination((prev) => ({
                                                            ...prev,
                                                            by: column.id,
                                                            dir: prev.dir === "asc" ? "desc" : "asc",
                                                            page: 1
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                        prepareRow(row);
                        const status = row.original.status?.toLowerCase()?.trim();
                        return (
                            <tr
                                style={
                                    status === "suspended"
                                        ? { backgroundColor: '#FF6B6B' }
                                        : status === "risksuspended"
                                        ? { backgroundColor: '#FFB74D' }
                                        : {}
                                }
                                {...row.getRowProps()}
                            >
                                {row.cells.map((cell) => {
                                    if (cell.column.id === 'status') {
                                        return (
                                            <td {...cell.getCellProps()}>
                                                <span className={`status-${cell.value.toLowerCase()}`}>
                                                    {cell.value}
                                                </span>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td {...cell.getCellProps()}>
                                            {cell.render("Cell")}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {!hidePagination && (
                <div className="pagination-container">
                    <button 
                        className="pagination-button"
                        onClick={() => gotoPage(0)}
                        disabled={!canPreviousPage}
                    >
                        First
                    </button>
                    <button 
                        className="pagination-button"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                    >
                        Previous
                    </button>
                    {pagination.map((page, i) => (
                        <button
                            key={i}
                            className={`pagination-button ${page === pageIndex + 1 ? 'active' : ''}`}
                            onClick={() => gotoPage(page - 1)}
                        >
                            {page}
                        </button>
                    ))}
                    <button 
                        className="pagination-button"
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                    >
                        Next
                    </button>
                    <button 
                        className="pagination-button"
                        onClick={() => gotoPage(pageCount - 1)}
                        disabled={!canNextPage}
                    >
                        Last
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomTable;
